// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
//
// Node-marker layout-zoom scaling.
//
// The library spreads the branch axis by 2**branchZoom and the step axis by
// 2**stepZoom (see core/scales.js zoomToScale). Those LAYOUT zooms re-lay-out the
// tree in world space while the camera holds still, so a fixed-world-radius node
// marker keeps a constant pixel size and looks tiny against the stretched tree.
// To keep markers proportional, grow their world radius with the layout spread —
// by the geometric mean of the two axis factors, so the radius tracks the square
// root of the per-node cell-area growth (a marker stays a constant fraction of
// its cell whether one axis stretches or both).
//
// Camera zoom (setZoom) already magnifies world-space content for free, so it is
// deliberately NOT part of this factor — only the layout zooms are.

const MIN_SCALE = 0.5; // never shrink a marker below half its base size
const MAX_SCALE = 3; // never balloon a marker past 3x its base size

function finite(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

/**
 * Layout-zoom multiplier for node-marker size, clamped to [MIN_SCALE, MAX_SCALE].
 *
 * @param {object} props - Resolved props (branchZoom, stepZoom).
 * @returns {number}
 */
export function nodeLayoutScale(props) {
  const branchZoom = finite(props.branchZoom, 0);
  const stepZoom = finite(props.stepZoom, 0);
  const factor = Math.pow(2, (branchZoom + stepZoom) / 2);
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, factor));
}

/**
 * Base node size (props.nodeSize, default 14) grown by the layout-zoom factor.
 *
 * @param {object} props - Resolved props (nodeSize, branchZoom, stepZoom).
 * @returns {number}
 */
export function effectiveNodeSize(props) {
  return (props.nodeSize ?? 14) * nodeLayoutScale(props);
}
